import cx from "classnames";
import React, { useEffect, useState } from "react";
import CmsMapCard from "../../../components/SignPostCards/CmsMapCard/CmsMapCard";
import { ArrowCircle } from "../../../components/common/Icons";
import { useCmsMap } from "../../../context";
import { Colours } from "../../../utils/colours";
import { scrollIntoView } from "../../../utils/scripts";
import WrappedSection from "../../WrappedSection/WrappedSection";
import InlineCmsMapLocationFilters from "../../filters/InlineCmsMapLocationFilters/InlineCmsMapLocationFilters";
import CmsMap from "../../map/CmsMap/CmsMap";
import * as styles from "./SectionCmsMap.module.scss";

const INITIAL_LOAD = 6;

export interface SectionCmsMapProps {
	mapType: string;
	cmsMapCards: Queries.CmsMapCardFragment[];
}

export const SectionCmsMap: React.FC<SectionCmsMapProps> = ({
	mapType,
	cmsMapCards,
}) => {
	const {
		onLoadCmsMap,
		onSelectCmsMap,
		cmsMapLocationsInRange,
		selectedCmsMapName,
	} = useCmsMap();

	const [currentPage, setCurrentPage] = useState(1);
	const startIndex = (currentPage - 1) * INITIAL_LOAD;
	const endIndex = startIndex + INITIAL_LOAD;

	const displayedCmsMapCards = cmsMapLocationsInRange
		?.sort((a) => (a.title === selectedCmsMapName ? -1 : 0))
		.slice(startIndex, endIndex);

	const totalPages = Math.ceil(cmsMapLocationsInRange?.length / INITIAL_LOAD);

	const handlePageChange = (newPage: number) => {
		const element = document.getElementById("cmsmaplistings");
		if (element) {
			const stickyNavOffset =
				document.getElementById("dt-header").offsetHeight + 16;
			const offset =
				element.getBoundingClientRect().top + window.scrollY - stickyNavOffset;
			window.requestAnimationFrame(() => {
				window.scrollTo({
					top: offset,
					behavior: "smooth",
				});
			});
		}

		setCurrentPage(newPage);
	};

	const handleFiltersUpdate = () => {
		setCurrentPage(1); // Reset the current page to 1
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: Only run on load
	useEffect(() => {
		onLoadCmsMap?.(cmsMapCards);
	}, [onLoadCmsMap]);

	return (
		<WrappedSection sectionName="SectionCmsMap">
			<h2 data-variation="hideFromJumpLinks" className={styles.title}>
				Search for a {mapType}
			</h2>
			<InlineCmsMapLocationFilters
				CmsMapLocationType={mapType}
				onFiltersUpdate={handleFiltersUpdate}
			/>
			<div className={styles.map}>
				<CmsMap />
			</div>
			<div id={"cmsmaplistings"} className={styles.listcontainer}>
				{displayedCmsMapCards?.length > 0 ? (
					displayedCmsMapCards?.map((card) => (
						<CmsMapCard
							key={card.title}
							onClick={() => {
								selectedCmsMapName !== card.title && onSelectCmsMap(card.title);
								scrollIntoView("cmsmap");
								setCurrentPage(1);
							}}
							title={card.title}
							subTitle={card.field_subtitle}
							location={card.field_map_card_location}
							secondaryInfoTitle={card.field_secondary_info_title}
							secondaryInfoText={card.field_secondary_info_text}
							tertiaryInfoText={card.field_tertiary_info_text?.value}
							googleMapsUrl={card.field_google_maps_link}
							ctaUrl={card.field_link?.uri}
							ctaLabel={card.field_link?.title}
						/>
					))
				) : (
					<p>
						Sorry, no results found. Please try expanding your search or viewing
						all results.
					</p>
				)}
			</div>
			{!!totalPages && totalPages > 1 && (
				<nav className={styles.pagination}>
					<button
						type="button"
						className={styles.previouspage}
						onClick={() => {
							handlePageChange(currentPage - 1);
						}}
						disabled={currentPage === 1}
						aria-label="Previous page"
						aria-hidden={currentPage === 1}
					>
						<ArrowCircle
							fill={Colours.MIDNIGHT}
							line={Colours.WHITE}
							rotation={180}
						/>
					</button>
					{Array.from({ length: totalPages }, (_, index) => (
						<button
							type="button"
							className={cx(
								styles.page,
								currentPage === index + 1 && styles.currentpage,
							)}
							key={index + 1}
							onClick={() => {
								handlePageChange(index + 1);
							}}
							disabled={currentPage === index + 1}
							aria-label={
								currentPage === index + 1
									? `Page ${index + 1} - current page`
									: `Visit page ${index + 1} of ${totalPages}`
							}
						>
							{index + 1}
						</button>
					))}
					<button
						type="button"
						className={styles.nextpage}
						onClick={() => {
							handlePageChange(currentPage + 1);
						}}
						disabled={currentPage === totalPages}
						aria-label="Next page"
						aria-hidden={currentPage === totalPages}
					>
						<ArrowCircle
							fill={Colours.MIDNIGHT}
							line={Colours.WHITE}
							rotation={0}
						/>
					</button>
				</nav>
			)}
		</WrappedSection>
	);
};
export default SectionCmsMap;
